import React, { useState, useEffect } from "react";
import { apiClient } from "../../apiclient/apiclient";
import PageContainer from "../../components/PageContainer";
import ThemedTable from "../../components/ThemedTable";
import { Download } from "lucide-react";

const TopProductsReport = () => {
  const [items, setItems] = useState([]);
  const [period, setPeriod] = useState("year");
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(
        `/top-products?period=${period}&limit=${limit}`
      );
      setItems(res.data || []);
    } catch (err) {
      console.error("Failed to fetch top items", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [period, limit]);

  const downloadExcel = async () => {
    try {
      setDownloading(true);
      const res = await apiClient.get(
        `/top-products?period=${period}&limit=${limit}&download=true`,
        { responseType: "blob" }
      );

      if (res.data.type === "application/json") {
        const textData = await res.data.text();
        console.error("Download Error:", textData);
        alert("Failed to download report: Server returned an error.");
        return;
      }

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Top_Products_Report_${period}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      alert("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-100px)] flex flex-col">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-4 px-6 pb-4 border-slate-200 bg-blue-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Top Selling Products</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track your best performing items over time
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 transition shadow-sm h-9"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
            <option value="all">All Time</option>
          </select>

          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 transition shadow-sm h-9"
          >
            <option value={10}>Top 10</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
          </select>

          <button
            onClick={downloadExcel}
            disabled={downloading}
            className="w-9 h-9 bg-[#172554] text-white rounded-lg hover:bg-[#1e2f6a] transition-all shadow-sm hover:shadow-md active:scale-90 flex items-center justify-center shrink-0 disabled:opacity-50"
            title="Download Excel"
          >
            {downloading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Download size={18} />
            )}
          </button>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      {/* ================= TABLE INFO BAR ================= */}
      <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            Top Products Details
          </h3>
          <span className="px-2 py-0.5 bg-blue-50 text-[#172554] text-[10px] font-bold rounded-full uppercase tracking-wider border border-blue-100">
            {items.length} Records
          </span>
        </div>
      </div>

      <div className="flex-1 bg-white overflow-hidden pb-8">
        <div className="w-full">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="w-full overflow-hidden">
              <ThemedTable className="w-full text-[13px]">
                <thead>
                  <tr className="bg-[#172554] text-white uppercase tracking-wider text-[11px]">
                    <th className="p-3 text-center w-10">#</th>
                    <th className="p-3 text-left">Product Name</th>
                    <th className="p-3 text-right">Quantity Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-20 text-center text-slate-400 font-medium italic">
                        No products found for this period.
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => {
                      const raw = item.image;
                      let base =
                        process.env.REACT_APP_IMAGE_URL ||
                        process.env.REACT_APP_BACKEND_URL ||
                        "http://localhost:5000";

                      base = base.replace(/\/uploads\/?$/i, "").replace(/\/$/, "");

                      let imgSrc = null;
                      if (raw) {
                        imgSrc = raw.startsWith("http")
                          ? raw
                          : `${base}${raw.startsWith("/") ? raw : `/uploads/${raw}`}`;
                      }

                      const name = item.productName || item.name || "Unknown";
                      const initial = name.charAt(0).toUpperCase();

                      return (
                        <tr
                          key={item.productId || item._id || index}
                          className="bg-white hover:bg-slate-100/80 transition-colors border-b border-slate-200 last:border-0"
                        >
                          <td className="px-2 py-3 text-center text-slate-400 font-medium">
                            {index + 1}
                          </td>
                          <td className="px-2 py-3 flex items-center gap-4">
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt={name}
                                className="w-10 h-10 rounded border border-slate-200 bg-white object-contain p-0.5 shrink-0"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold border border-blue-200 shrink-0">
                                {initial}
                              </div>
                            )}
                            <span className="font-semibold text-slate-700 line-clamp-3 whitespace-normal break-words flex-1">
                              {name}
                            </span>
                          </td>
                          <td className="px-2 py-3 text-right font-bold text-slate-900">
                            {item.quantitySold} <span className="text-slate-400 font-medium ml-1 text-[10px] uppercase tracking-wide">pcs</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </ThemedTable>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopProductsReport;
